import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import Button from 'dpl/common/design_system/Button';
import { RESULTS_TYPES } from 'dpl/puppy_search_results/utils/constants';
import useSearchResultsContext from 'dpl/puppy_search_results/hooks/useSearchResultsContext';
import { isMobileUA } from 'dpl/shared/utils';

import useSearchFiltering from '../hooks/useSearchFiltering';

const IS_MOBILE_UA = isMobileUA();
const DEFAULT_BUTTON_WIDTH = '110px';

export default function FilterButtonActions({
  className,
  setIsMenuOpen,
  totalItemCount,
  resultsType,
  resetButtonText,
  buttonSize,
  onClick
}) {
  const { hasFilters, clearFilters } = useSearchFiltering();
  const { isLoading } = useSearchResultsContext();

  const [resultsButtonWidth, setResultsButtonWidth] =
    useState(DEFAULT_BUTTON_WIDTH);
  const textMeasureRef = useRef(null);

  const puppyTitle = totalItemCount === 1 ? 'puppy' : 'puppies';
  const breederTitle = totalItemCount === 1 ? 'breeder' : 'breeders';

  const itemType =
    resultsType === RESULTS_TYPES.PUPPIES ? puppyTitle : breederTitle;

  function handleApplyFilters() {
    setIsMenuOpen();
    onClick?.();
  }

  const hasNoResults = !isLoading && totalItemCount === 0;
  const resultsText = hasNoResults
    ? 'No results'
    : `Show ${totalItemCount} ${itemType}`;

  useEffect(() => {
    if (!isLoading && textMeasureRef.current) {
      setResultsButtonWidth(
        textMeasureRef.current.offsetWidth || DEFAULT_BUTTON_WIDTH
      );
    }
  }, [isLoading, resultsText]);

  return (
    <div className={classnames('flex gap-8', className)}>
      <Button
        variant="ghostGray"
        size={buttonSize}
        className="font-16 nowrap pv3"
        onClick={clearFilters}
        disabled={!hasFilters}
      >
        {resetButtonText}
      </Button>
      <Button
        type="button"
        variant="primary"
        size={buttonSize}
        className={classnames('relative font-16 nowrap w-100 w-auto-ns', {
          pv3: !isLoading,
          pv2: isLoading
        })}
        disabled={isLoading || hasNoResults}
        onClick={handleApplyFilters}
      >
        <div
          style={
            IS_MOBILE_UA
              ? undefined
              : { width: resultsButtonWidth, transition: 'width 0.2s ease' }
          }
        >
          {!isLoading ? (
            resultsText
          ) : (
            <div className="flex justify-center">
              <div className="filter-loading-spinner" />
            </div>
          )}
        </div>

        {/* 
          this span is only used to measure width of the results text
          to use for the transition and should not affect the layout
        */}
        <span
          ref={textMeasureRef}
          className="absolute top-0 right-0 v-hidden nowrap"
          style={{ height: 0 }}
        >
          {resultsText}
        </span>
      </Button>
    </div>
  );
}

FilterButtonActions.propTypes = {
  className: PropTypes.string,
  setIsMenuOpen: PropTypes.func.isRequired,
  totalItemCount: PropTypes.number.isRequired,
  resultsType: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  resetButtonText: PropTypes.string,
  buttonSize: PropTypes.string
};

FilterButtonActions.defaultProps = {
  className: null,
  onClick: null,
  resetButtonText: 'Reset',
  buttonSize: 'xsmall'
};
